import { h, Component } from 'preact';
import Store from 'store/store';

import Button from 'components/bolt/button/button';
import { joinClasses } from 'utils/utils';
import { expandFilterSelection, getMovieName, getFiltersOrderReference } from 'utils/bolt/showtimes';
import formatDateTime from 'utils/formatDateTime';
import Close from 'components/bolt/icons/close';

import s from 'components/bolt/showtimes/filterSelection2.sss';

export default class FilterSelection extends Component {
	constructor(props) {
		super(props);

		this.today = formatDateTime(new Date(), 'yyyy-MM-dd');

		this.onClose = this.onClose.bind(this);
	}

	onClose() {
		this.props.onClose();
		Store.emit('FILTERS_RESET');
	}

	isDefault() {
		let filters = this.props.filters;
		let selected = filters.selected;
		if (!selected) {
			return true;
		}
		let firstDate = filters.available && filters.available.date && filters.available.date[0];
		return Object.keys(selected).every(filter => {
			let sel = selected[filter];
			if (!sel || !sel.length) {
				return true;
			}
			return filter === 'date' && sel.length === 1 && (sel[0] === firstDate || sel[0] === this.today);
		});
	}

	getDisplayedFilters(available) {
		let allKeys = Object.keys(available);
		allKeys.sort((a, b) => {
			if (a === 'movie') return -1;
			if (b === 'movie') return 1;
			return 0;
		});

		// const showAudioDetailsAsFormat = Store.get().appData?.pages?.boltShowtimes?.data?.showAudioDetailsAsFormat;
		return allKeys.filter(e => e !== 'date' && e !== 'exhibitor' && available[e]?.length).map(f => {
			if (f === 'audioDetails') {
				return { filter: f, mergeWith: 'format' };
			}
			return { filter: f };
		});
	}

	getFilterName(filter, option) {
		const filterCopy = this.props.appData.copy?.baseCopy?.filters?.[filter] || {};
		const options = filterCopy.useLangOptions ? this.props.appData.copy.baseCopy.languages : filterCopy;

		if (filter === 'movie') {
			return getMovieName(option);
		}

		return options[option] || option;
	}

	renderFilters() {
		let { filters, appData } = this.props;
		const fontStyles = appData.styling.fontVariables;
		const accessibilityCopy = appData.copy.accessibilityCopy;
		const noFiltersCopy = appData.copy.baseCopy.$FILTERS_NONE_AVAILABLE || 'No filters available';
		
		const hideFilterOptionTime = appData.pages?.boltShowtimes?.data?.hideFilterOptionTime;

		if (filters.loading || !filters.available) {
			return;
		}

		let filtersList = this.getDisplayedFilters(filters.available)?.map(f => typeof f === 'string' ? { filter: f } : f)?.filter(e => e);
		if (!filtersList?.length) {
			return (
				<div class={s.empty}>{noFiltersCopy}</div>
			);
		}

		let getSelected = (() => {
			let cache = {};
			return (filter) => cache[filter] || (cache[filter] = expandFilterSelection(filters.selected?.[filter] || [], filters.available?.[filter]));
		})();

		return filtersList.map(({ filter, mergeWith }) => {
			if (mergeWith && filtersList.some(f => f.filter === mergeWith)) {
				return;
			}
			let options = filters.available[filter];

			let merges = filtersList.filter(f => f?.mergeWith === filter);
			if (merges.length) {
				options = [filter, ...merges.map(m => m.filter)].flatMap(f => filters.available[f]?.map(opt => ({ value: opt, filter: f })));
			}

			let optionAriaLabel = '';
			if (filter === 'time') {
				if (hideFilterOptionTime) return;
				optionAriaLabel = accessibilityCopy.$TIME_SELECTION_ARIA;

				const order = ['morning', 'afternoon', 'evening', 'late'];
				options = order.filter(tod => options?.includes(tod));
			}

			if (!options.length || options.length <= 1) {
				return;
			}

			let displayedOptions = Object.values(options.reduce((all, option) => {
				if (typeof option === 'string') {
					option = { value: option };
				}
				let currentFilter = option.filter || filter;
				let name = this.getFilterName(currentFilter, option.value);

				let active = getSelected(currentFilter).includes(option.value);
				if (!all[name]) {
					all[name] = { name, active, source: [], filter: currentFilter };
				}
				all[name].source.push(option.value);
				all[name].active = all[name].active || active;
				return all;
			}, {}));
			
			if (filter === 'format') {
				optionAriaLabel = accessibilityCopy.$FORMAT_SELECTION_ARIA;
			}

			if (filter === 'format' || filter === 'movie') {
				displayedOptions = displayedOptions.sort((a, b) => {
					const orderingReference = getFiltersOrderReference(filter === 'format' ? 'format' : 'movieId');
					if (orderingReference.length) {
						const orderA = orderingReference.indexOf(a.source[0]);
						const orderB = orderingReference.indexOf(b.source[0]);
						if (orderA > -1 || orderB > -1) {
							return orderB - orderA;
						}
					}
					return a.name.localeCompare(b.name);
				});
			}

			displayedOptions.unshift({
				active: getSelected(filter).length === 0,
				name: appData?.copy?.baseCopy?.$ALL || 'All',
				source: 'all'
			});

			const filterCopy = appData.copy?.baseCopy?.filters?.[filter] || {};
			const filterName = filterCopy?._name || filter;

			return (
				<div class={s.filter} key={filter}>
					<div class={joinClasses(s.name, fontStyles.filterOverlayFilterTitle)}>
						<span>{filterName}</span>
					</div>
					<div class={s.options}>
						{displayedOptions.map(opt => (
							<Button
								type="tag"
								class={joinClasses(s.option, opt.active && s.active, fontStyles.filterMenuFilter)}
								active={opt.active}
								trigger={() => {
									opt.source === 'all' ?
										Store.emit('FILTER_RESET_SINGLE', filter) :
										Store.emit('FILTER_TOGGLE', opt.filter || filter, opt.source);
									Store.emit('FILTER_SCROLL_INTO_VIEW', opt.source);
								}}
								ariaLabel={optionAriaLabel.replace('<option>', opt.name)}
							>
								{opt.name}
							</Button>
						))}
					</div>
				</div>
			);
		});
	}

	render(props) {
		const { open, appData, filters } = props;
		const filtersCopy = appData.copy?.baseCopy?.$FILTERS || 'Filters';
		const closeCopy = appData.copy?.baseCopy?.$FILTERS_CLOSE || 'Close';
		const accessibilityCopy = appData.copy.accessibilityCopy || {};
		const fontStyles = appData.styling?.fontVariables;
		const isRTL = fontStyles.textDirection === 'rtl';
		let submitCopy = appData.copy?.baseCopy?.$VIEW_SHOWTIMES || 'View Showtimes';

		if (!filters.loading && !this.getDisplayedFilters(filters.available).length) {
			submitCopy = closeCopy;
		}

		return (
			<div class={joinClasses(s.wrapper, open && s.open)}>
				<div class={s.shadow} onClick={this.onClose} />
				<div class={s.container}>
					<div class={s.scroller}>
						<div class={s.content}>
							<div class={s.header}>
								<h4 class={joinClasses(s.title, fontStyles.filterOverlayTitle)}>{filtersCopy}</h4>
								<Close class={s.close} onClick={this.onClose} />
							</div>
							<div class={s.filters}>
								{this.renderFilters()}
							</div>
							<div class={joinClasses(s.footer, isRTL && s.rtlFooter)}>
								<Button
									type="primary"
									class={joinClasses(s.submit, fontStyles.filterMenuViewShowtimes)}
									trigger={props.onSubmit}
									ariaLabel={accessibilityCopy.$VIEW_SHOWTIMES_ARIA}
								>
									{submitCopy}
								</Button>
							</div>
						</div>
					</div>
				</div>
			</div>
		);
	}
}
